import * as React from 'react'
import { isFragment } from 'react-is'
import clsx from 'clsx'
import { SxProps, sx as sxFunc } from '../sx'
import style from './index.module.scss'
import { capitalize } from '../../utils'

export function isValueSelected<T>(value: T, candidate: T[]) {
  if (candidate === undefined || value === undefined) {
    return false
  }

  if (Array.isArray(candidate)) {
    return candidate.indexOf(value) >= 0
  }

  return value === candidate
}

export interface ToggleButtonGroupClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `orientation="vertical"`. */
  vertical: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** Styles applied to the children. */
  grouped: string
  /** Styles applied to the children if `orientation="horizontal"`. */
  orientationHorizontal: string
  /** Styles applied to the children if `orientation="vertical"`. */
  orientationVertical: string
}

export interface ToggleButtonGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'children'> {
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<ToggleButtonGroupClasses>
  /**
   * The color of the button when it is selected.
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  /**
   * The variant to use.
   * @default 'contained'
   */
  variant?: 'contained' | 'outlined' | 'text'
  /**
   * If `true`, only allow one of the child ToggleButton values to be selected.
   * @default false
   */
  exclusive?: boolean
  /**
   * If `true`, the component is disabled. This implies that all ToggleButton children will be disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, the button group will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean
  /**
   * Callback fired when the value changes.
   *
   * @param {React.MouseEvent<HTMLElement>} event The event source of the callback.
   * @param {any} value of the selected buttons. When `exclusive` is true
   * this is a single value; when false an array of selected values. If no value
   * is selected and `exclusive` is true the value is null; when false an empty array.
   */
  onChange?: (event: React.MouseEvent<HTMLElement>, value: any) => void
  /**
   * The component orientation (layout flow direction).
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical'
  /**
   * The size of the component.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The currently selected value within the group or an array of selected
   * values when `exclusive` is false.
   *
   * The value must have reference equality with the option in order to be selected.
   */
  value?: any
}

export const ToggleButtonGroup = React.forwardRef<
  HTMLDivElement,
  ToggleButtonGroupProps
>(function ToggleButtonGroup(props, ref) {
  const {
    children,
    className,
    classes,
    color = 'primary',
    variant = 'contained',
    disabled = false,
    exclusive = false,
    fullWidth = false,
    onChange,
    orientation = 'horizontal',
    size = 'medium',
    value,
    sx,
    ...other
  } = props

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    buttonValue: any
  ) => {
    if (!onChange) {
      return
    }

    const index = value && value.indexOf(buttonValue)
    let newValue

    if (value && index >= 0) {
      newValue = value.slice()
      newValue.splice(index, 1)
    } else {
      newValue = value ? value.concat(buttonValue) : [buttonValue]
    }

    onChange(event, newValue)
  }

  const handleExclusiveChange = (
    event: React.MouseEvent<HTMLElement>,
    buttonValue: any
  ) => {
    if (!onChange) {
      return
    }

    onChange(event, value === buttonValue ? null : buttonValue)
  }

  return (
    <div
      role="group"
      className={clsx(
        style.root,
        classes?.root,
        style[`orientation-${orientation}`],
        // @ts-ignore
        classes?.[`orientation${capitalize(orientation)}`],
        className
      )}
      ref={ref}
      {...other}
    >
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) {
          return null
        }

        if (process.env.NODE_ENV !== 'production') {
          if (isFragment(child)) {
            console.error(
              [
                "The ToggleButtonGroup component doesn't accept a Fragment as a child.",
                'Consider providing an array instead.',
              ].join('\n')
            )
          }
        }

        return React.cloneElement(child, {
          //@ts-ignore
          className: clsx(
            style.grouped,
            classes?.grouped,
            child.props.className,
            sxFunc(sx)
          ),
          classes: {
            root: clsx(classes?.grouped, child.props?.classes?.root),
            disabled: clsx(classes?.disabled, child.props?.classes?.disabled),
            selected: clsx(style.selected, child.props?.classes?.selected),
          },
          onChange: exclusive ? handleExclusiveChange : handleChange,
          selected:
            child.props.selected === undefined
              ? isValueSelected(child.props.value, value)
              : child.props.selected,
          size,
          fullWidth,
          color: child.props.color || color,
          variant: child.props.variant || variant,
          disabled: child.props.disabled || disabled,
        })
      })}
    </div>
  )
})
